import React, { useEffect, useState, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';

// CONTEXT
import { AuthProvider } from 'context/AuthContext';
import { ToastProvider } from 'context/ToastContext';
import { ThemeProvider } from 'context/ThemeContext';
import { TileStyleProvider } from 'context/TileStyleContext';
import { SeasonThemeProvider } from 'context/SeasonThemeContext';
import { MenuProvider } from 'context/MenuContext';
import { ConnectionProvider } from 'context/ConnectionContext';
import { UserDataProvider } from 'context/UserDataContext';

// PAGES
import Home from 'pages/home';
import Maintenance from 'pages/Maintenance';

// COMPONENTS
import ProtectedRoute from 'components/protectedRoute';
import Loader from 'components/loaderComponent';
import HubStatusCheck from 'components/HubStatusCheck';
import SeasonalEffects from 'components/effects';
import GlobalKeyboardShortcuts from 'components/GlobalKeyboardShortcuts';
import UpdateNotification from 'components/UpdateNotification';

// UTILS
import checkForUpdates from 'utils/checkForUpdates';
import { checkBackendHealth } from 'services/healthCheck';

// STYLES
import 'assets/css/SeasonalEffects.css';

// Pages secondaires chargées à la demande (chunks séparés) : elles tirent des
// dépendances lourdes (admin : framer-motion, @dnd-kit, socket.io, image-crop)
// qui n'ont pas à peser sur le chargement initial de la Home.
const EditInterface = lazy(() => import('pages/editInterface'));
const Admin = lazy(() => import('pages/admin'));
const ErrorPage = lazy(() => import('pages/error'));
const NotFound = lazy(() => import('pages/notFound'));

function App() {
    const [backendStatus, setBackendStatus] = useState('ok');
    const [isChecking, setIsChecking] = useState(true);

    useEffect(() => {
        const checkHealth = async () => {
            const status = await checkBackendHealth();
            setBackendStatus(status);
            setIsChecking(false);
        };
        checkHealth();
    }, []);

    const { updateAvailable, applyUpdate } = checkForUpdates();

    const maintenanceMessage = backendStatus === 'rate_limited'
        ? "Le serveur reçoit trop de requêtes en ce moment. Veuillez patienter quelques minutes puis réessayer."
        : undefined; // laisse le message par défaut de <Maintenance />

    return (
        <SeasonThemeProvider>
            {isChecking ? (
                <Loader />
            ) : backendStatus !== 'ok' ? (
                <Maintenance message={maintenanceMessage} />
            ) : (
                <AuthProvider>
                    <HubStatusCheck>
                        <ToastProvider>
                            <ThemeProvider>
                                <TileStyleProvider>
                                    <MenuProvider>
                                        <ConnectionProvider>
                                            <Router>
                                                <UserDataProvider>
                                                    <GlobalKeyboardShortcuts />
                                                    <Suspense fallback={<Loader />}>
                                                        <Routes>
                                                            <Route path="/" element={<Home />} />
                                                            <Route path="/edit-interface" element={
                                                                <ProtectedRoute>
                                                                    <EditInterface />
                                                                </ProtectedRoute>
                                                            } />
                                                            <Route path="/admin/*" element={
                                                                <ProtectedRoute adminOnly={true}>
                                                                    <Admin />
                                                                </ProtectedRoute>
                                                            } />
                                                            <Route path="/error" element={<ErrorPage />} />
                                                            <Route path="*" element={<NotFound />} />
                                                        </Routes>
                                                    </Suspense>
                                                </UserDataProvider>
                                            </Router>
                                            <SeasonalEffects />
                                        </ConnectionProvider>
                                    </MenuProvider>
                                </TileStyleProvider>
                            </ThemeProvider>
                        </ToastProvider>
                    </HubStatusCheck>
                </AuthProvider>
            )}

            {updateAvailable && (
                <UpdateNotification onUpdate={applyUpdate} />
            )}
        </SeasonThemeProvider>
    );
}

export default App;